/**
 * ChatProvider — auto-wires shared side effects for any chat page.
 *
 * The new equivalent of the old ChatProvider that lived in ChatContext.tsx.
 * Drop it inside any Chat.Root and every page gets credit gating,
 * agent intent processing, and project store sync for free.
 *
 * @example
 * ```tsx
 * <Chat.Root streamOptions={streamOptions}>
 *   <ChatProvider name="website" syncKeys={["websiteId", "projectId"]} />
 *   <PageContent />
 * </Chat.Root>
 * ```
 */
import {
  useOutOfCreditsDetection,
  useAgentIntentWatcher,
  useSyncToProjectStore,
  type SyncKey,
} from "@hooks/shared";

interface ChatProviderProps {
  /** Name for the agent intent processor (e.g., "website", "brainstorm") */
  name: string;
  /** Which keys from stream values to sync to the project store */
  syncKeys?: SyncKey[];
}

export function ChatProvider({ name, syncKeys }: ChatProviderProps) {
  useOutOfCreditsDetection();
  useAgentIntentWatcher(name);
  if (syncKeys) useSyncToProjectStore(syncKeys);
  return null;
}
